import React from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "font-awesome/css/font-awesome.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BrowserRouter as Router } from "react-router-dom";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  from,
  ServerParseError,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import useAppContext, { AppContext } from "./context/AppContext";
import { auth } from "./utils/firebase";

const RootApp: React.FC = () => {
  const value = useAppContext();
  const httpLink = new HttpLink({
    uri: `${process.env.REACT_APP_API_URL}v1/gql`,
  });

  const errorLink = onError(({ networkError, operation, forward }) => {
    if ((networkError as ServerParseError)?.statusCode === 403) {
      return new Observable((observer) => {
        auth.currentUser
          ?.getIdToken(true)
          .then(function (idToken) {
            if (!idToken) {
              client.resetStore();
              localStorage.clear();
              value.setUser({
                user: {},
                isUserLoggedIn: false,
                token: "",
                isInitialized: true,
              });

              return;
            }

            localStorage.setItem("token", idToken);

            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                Authorization: "Bearer " + idToken || "",
              },
            }));
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          })
          .catch((error) => {
            client.resetStore();
            localStorage.clear();
            value.setUser({
              user: {},
              isUserLoggedIn: false,
              token: "",
              isInitialized: true,
            });
            observer.error(error);
          });
      });
    }
  });

  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization: "Bearer " + localStorage.getItem("token") || null,
      },
    }));
    return forward(operation);
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authMiddleware, errorLink, httpLink]),
  });

  return (
    <ApolloProvider client={client}>
      <AppContext.Provider value={value}>
        <Router>
          <App />
        </Router>
      </AppContext.Provider>
    </ApolloProvider>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>,
  document.getElementById("root"),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
